import { useFormik } from "formik";
import { DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import {
  useCreateFinanceAccountMutation,
  useFetchBanksFinanceAccountsQuery,
  useFetchTypeFinanceAccountsQuery,
  useToast,
} from "@/hooks";
import { CreateFinanceAccountRequestDTO } from "@/types";
import { FinanceAccountValidationSchema } from "@/shared";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@/components/ui/select";
import { MessageError } from "../../message-error";
import { DisplayError } from "@/utils";

type Props = {
  handleClose: () => void;
};

export function FormCreateFinanceAccount({ handleClose }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync, isLoading } = useCreateFinanceAccountMutation();

  const initialValues = {
    description: "",
    number: "",
    agency: "",
    comments: "",
    bankId: "",
    typeId: "",
    shared: 0,
    owner: "",
  };

  const { data: dataTypeAccounts, isLoading: isLoadingTypeAccounts } =
    useFetchTypeFinanceAccountsQuery();
  const { data: dataBanks, isLoading: isLoadingBanks } =
    useFetchBanksFinanceAccountsQuery();

  const { handleSubmit, getFieldProps, errors, touched, resetForm, ...formik } =
    useFormik<CreateFinanceAccountRequestDTO>({
      initialValues,
      validationSchema: FinanceAccountValidationSchema(),
      onSubmit: async (accountData) => {
        await createFinanceAccount(accountData);
      },
    });

  async function createFinanceAccount(
    accountData: CreateFinanceAccountRequestDTO
  ) {
    try {
      await mutateAsync(accountData, {
        onSuccess: ({ message }) => {
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message}`),
          });
          handleClose();
          resetForm();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }
  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="h-full overflow-y-scroll flex flex-col">
        <DrawerHeader className="flex-none">
          <DrawerTitle className="text-teal-700 font-bold">
            Nova conta
          </DrawerTitle>
          <DrawerDescription>
            Preencha os campos para criar uma nova conta financeira.
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <div className="flex flex-col gap-2">
            <Label>{t("LABEL.name")}</Label>
            <div className="space-y-1">
              <Input
                isInvalid={touched.description && errors.description}
                isValid={touched.description && !errors.description}
                {...getFieldProps("description")}
              />
              {touched.description && errors.description ? (
                <span className="text-red-500 text-sm min-h-3">
                  {errors.description}
                </span>
              ) : (
                <div className="min-h-3"></div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-x-3 gap-y-5">
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.type")}</Label>
              <div className="space-y-1">
                <Select
                  {...getFieldProps("typeId")}
                  onValueChange={(value) => {
                    formik.setFieldValue("typeId", value);
                  }}
                >
                  <SelectTrigger
                    isInvalid={touched.typeId && errors.typeId !== undefined}
                    isValid={touched.typeId && errors.typeId === undefined}
                    loading={isLoadingTypeAccounts || !dataTypeAccounts}
                  >
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {dataTypeAccounts &&
                        Array.isArray(dataTypeAccounts) &&
                        dataTypeAccounts.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={errors.typeId} />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label>Visibilidade</Label>
              <div className="space-y-1">
                <Select
                  {...getFieldProps("shared")}
                  onValueChange={(value) => {
                    formik.setFieldValue("shared", value);
                  }}
                  defaultValue="0"
                >
                  <SelectTrigger
                    isInvalid={touched.shared && errors.shared}
                    isValid={touched.shared && !errors.shared}
                  >
                    <SelectValue placeholder="Selecione a visibilidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="0">Pública</SelectItem>
                      <SelectItem value="1">Privada</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={errors.shared} />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label>Banco</Label>
              <div className="space-y-1">
                <Select
                  {...getFieldProps("bankId")}
                  onValueChange={(value) => {
                    formik.setFieldValue("bankId", value);
                  }}
                >
                  <SelectTrigger
                    isInvalid={touched.bankId && errors.bankId !== undefined}
                    isValid={touched.bankId && errors.bankId === undefined}
                    loading={isLoadingBanks || !dataBanks}
                  >
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {dataBanks &&
                        Array.isArray(dataBanks) &&
                        dataBanks.map((bank) => (
                          <SelectItem key={bank.id} value={bank.id}>
                            {bank.name}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={errors.bankId} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label>Descrição</Label>
            <Textarea
              placeholder="Descrição"
              isInvalid={touched.comments && errors.comments}
              isValid={touched.comments && !errors.comments}
              {...getFieldProps("comments")}
              messageError={errors.comments}
            />
          </div>
        </div>

        <DrawerFooter className=" flex flex-row justify-center mb-5  pb-10 gap-2">
          <Button
            type="submit"
            className="px-10 "
            disabled={isLoading || !formik.isValid}
            isLoading={isLoading}
          >
            {t("LABEL.save")}
          </Button>
          <DrawerClose>
            <Button variant="outline">Cancelar</Button>
          </DrawerClose>
        </DrawerFooter>
      </div>
    </form>
  );
}
